import _ from 'underscore';
import BaseExporter from './BaseExporter';
import TimecodeConverter from '../utils/TimecodeConverter';
import ti18n from 'i18n!nls/ExportPopupi18n';

/**
 * FcpxmlExporter - Export timeline data to Final Cut Pro XML format
 * 
 * Features:
 * - FCPXML 1.8 format (Final Cut Pro X compatible)
 * - Multi-lane support for multiple source files
 * - Proper asset management and references
 * - Frame-accurate timing with FCPXML duration format
 * - DaVinci Resolve compatibility (imports FCPXML format)
 */
const FcpxmlExporter = BaseExporter.extend({

    /**
     * Export timeline data as FCPXML format
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options  
     * @returns {String} FCPXML content as XML
     */
    export: function(timelineData, options) {
        console.log('🎬 FCPXML EXPORT STARTED: FcpxmlExporter.export() called with', timelineData.length, 'clips');
        console.log('🎬 FCPXML EXPORT: Build timestamp check - this should be the NEW code with independent lanes');
        
        options = this.validateAndExtendOptions(options);
        this.validateTimelineData(timelineData);

        try {
            // Generate FCPXML header
            let fcpxmlContent = `<?xml version="1.0" encoding="UTF-8"?>\n`;
            fcpxmlContent += `<fcpxml version="1.11">\n`;
            fcpxmlContent += `  <resources>\n`;

            // Create asset map for unique source files
            const uniqueFiles = new Map();
            let assetCounter = 1;
            
            // First pass: create unique assets
            timelineData.forEach(clip => {
                if (clip.fileName && !uniqueFiles.has(clip.fileName)) {
                    const assetId = `asset${assetCounter}`;
                    const assetName = clip.fileName.replace(/\.[^/.]+$/, ""); // Remove extension
                    const sourceFilePath = this.getMediaFilePath(clip, options);
                    uniqueFiles.set(clip.fileName, { assetId, assetName, sourceFilePath });
                    assetCounter++;
                }
            });

            // Add format resource definition first
            const videoFormat = this._getVideoFormatString(options.frameRate);
            fcpxmlContent += `    <format id="${videoFormat}" name="${this._getVideoFormatName(options.frameRate)}" frameDuration="${this._getFrameDuration(options.frameRate)}" width="1920" height="1080" colorSpace="1-1-1 (Rec. 709)"/>\n`;

            // Generate resource assets with proper structure
            for (const [fileName, assetInfo] of uniqueFiles) {
                const srcPath = this._formatAssetSourcePath(assetInfo.sourceFilePath);
                
                // Find a clip using this asset to get duration and media info
                const sampleClip = timelineData.find(clip => clip.fileName === fileName);
                const duration = sampleClip ? TimecodeConverter.convertSecondsToFCPXMLDuration(sampleClip.durationInSeconds, options.frameRate) : "0/25s";
                const hasVideo = sampleClip && sampleClip.hasVideo;
                
                if (hasVideo && options.useEnhancedAudio !== false) {
                    // Create separate video-only asset (muted) and enhanced audio asset
                    const videoAssetId = `${assetInfo.assetId}_video`;
                    const audioAssetId = `${assetInfo.assetId}_audio`;
                    
                    // Video-only asset (muted)
                    fcpxmlContent += `    <asset id="${this.escapeXml(videoAssetId)}" name="${this.escapeXml(fileName)}" start="0s" duration="${duration}" hasVideo="1" hasAudio="0" format="${videoFormat}">\n`;
                    fcpxmlContent += `      <media-rep kind="original-media" src="${this.escapeXml(srcPath)}"/>\n`;
                    fcpxmlContent += `    </asset>\n`;
                    
                    // Enhanced audio-only asset
                    const enhancedAudioPath = this._getEnhancedAudioPath(fileName, srcPath);
                    fcpxmlContent += `    <asset id="${this.escapeXml(audioAssetId)}" name="${this.escapeXml(fileName + '_enhanced_audio')}" start="0s" duration="${duration}" hasVideo="0" hasAudio="1" format="${videoFormat}">\n`;
                    fcpxmlContent += `      <media-rep kind="original-media" src="${this.escapeXml(enhancedAudioPath)}"/>\n`;
                    fcpxmlContent += `    </asset>\n`;
                } else {
                    // Single asset for audio-only files or when enhanced audio is disabled
                    const hasVideoAttr = hasVideo ? "1" : "0";
                    const hasAudioAttr = "1";
                    
                    fcpxmlContent += `    <asset id="${this.escapeXml(assetInfo.assetId)}" name="${this.escapeXml(fileName)}" start="0s" duration="${duration}" hasVideo="${hasVideoAttr}" hasAudio="${hasAudioAttr}" format="${videoFormat}">\n`;
                    fcpxmlContent += `      <media-rep kind="original-media" src="${this.escapeXml(srcPath)}"/>\n`;
                    fcpxmlContent += `    </asset>\n`;
                }
            }

            fcpxmlContent += `  </resources>\n`;
            fcpxmlContent += `  <library>\n`;
            fcpxmlContent += `    <event>\n`;
            fcpxmlContent += `      <project name="${this.escapeXml(options.projectTitle)}">\n`;
            
            // Use the videoFormat already declared above
            const totalDuration = this.getTotalDuration(timelineData);
            const durationString = TimecodeConverter.convertSecondsToFCPXMLDuration(totalDuration, options.frameRate);
            
            fcpxmlContent += `        <sequence format="${videoFormat}" duration="${durationString}">\n`;
            fcpxmlContent += `          <spine>\n`;

            // Generate nested multi-lane structure like the competitor
            fcpxmlContent += this._generateNestedSpineStructure(timelineData, uniqueFiles, options);

            fcpxmlContent += `          </spine>\n`;
            fcpxmlContent += `        </sequence>\n`;
            fcpxmlContent += `      </project>\n`;
            fcpxmlContent += `    </event>\n`;
            fcpxmlContent += `  </library>\n`;
            fcpxmlContent += `</fcpxml>\n`;

            return fcpxmlContent;

        } catch (error) {
            console.error('Error generating FCPXML:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Generate proper hierarchical spine structure for Final Cut Pro X
     * Creates nested structure where audio clips are children of video clips or gaps
     * @private
     * @param {Array} timelineData - Timeline clip data
     * @param {Map} uniqueFiles - Asset information map
     * @param {Object} options - Export options
     * @returns {String} Spine XML content with hierarchical structure
     */
    _generateNestedSpineStructure: function(timelineData, uniqueFiles, options) {
        console.log('🎬 FCPXML HIERARCHICAL: Starting nested structure generation with', timelineData.length, 'clips');

        if (timelineData.length === 0) {
            console.log('🎬 FCPXML HIERARCHICAL: No timeline data, returning empty');
            return '';
        }

        let spineContent = '';

        // Sort all clips by start time
        const sortedClips = [...timelineData].sort((a, b) => a.startTimeInSeconds - b.startTimeInSeconds);

        // Separate video and audio clips
        const videoClips = sortedClips.filter(clip => clip.hasVideo);
        const audioClips = sortedClips.filter(clip => !clip.hasVideo);

        console.log(`🎬 FCPXML HIERARCHICAL: ${videoClips.length} video clips, ${audioClips.length} audio-only clips`);

        // Process timeline sequentially, creating video clips with nested audio or gaps with audio
        let currentTime = 0;
        let videoClipIndex = 0;
        let negativeLayerCounter = -1; // Start at -1 for first nested audio

        while (videoClipIndex < videoClips.length) {
            const videoClip = videoClips[videoClipIndex];

            // Check if there's a gap before this video clip
            if (videoClip.startTimeInSeconds > currentTime + 0.1) {
                // Generate gap with any overlapping audio
                spineContent += this._generateGapWithNestedAudio(
                    currentTime,
                    videoClip.startTimeInSeconds,
                    audioClips,
                    uniqueFiles,
                    options
                );
                currentTime = videoClip.startTimeInSeconds;
            }

            // Generate video clip with nested audio
            const { xml, audioLayerCount } = this._generateVideoClipWithNestedAudio(
                videoClip,
                audioClips,
                uniqueFiles,
                options
            );
            spineContent += xml;

            currentTime = videoClip.startTimeInSeconds + videoClip.durationInSeconds;
            videoClipIndex++;
        }

        // Handle any remaining audio-only content after the last video clip
        const totalDuration = this.getTotalDuration(timelineData);
        if (currentTime < totalDuration - 0.1) {
            spineContent += this._generateGapWithNestedAudio(
                currentTime,
                totalDuration,
                audioClips,
                uniqueFiles,
                options
            );
        }

        console.log('🎬 FCPXML HIERARCHICAL: Hierarchical structure generation complete');

        return spineContent;
    },

    /**
     * Generate a video clip element with nested audio clips as children
     * @private
     * @param {Object} videoClip - Video clip object
     * @param {Array} audioClips - All audio-only clips
     * @param {Map} uniqueFiles - Asset information map
     * @param {Object} options - Export options
     * @returns {Object} { xml: string, audioLayerCount: number }
     */
    _generateVideoClipWithNestedAudio: function(videoClip, audioClips, uniqueFiles, options) {
        const assetInfo = uniqueFiles.get(videoClip.fileName);
        if (!assetInfo) {
            console.log('🎬 FCPXML HIERARCHICAL: No asset info for video', videoClip.fileName);
            return { xml: '', audioLayerCount: 0 };
        }

        const duration = TimecodeConverter.convertSecondsToFCPXMLDuration(videoClip.durationInSeconds, options.frameRate);
        const offset = TimecodeConverter.convertSecondsToFCPXMLDuration(videoClip.startTimeInSeconds, options.frameRate);
        const start = TimecodeConverter.convertSecondsToFCPXMLDuration(videoClip.sourceStartInSeconds, options.frameRate);

        const videoFormat = this._getVideoFormatString(options.frameRate);
        const assetRef = this._getAssetReference(videoClip, assetInfo, options);

        let xml = `            <asset-clip videoRole="video.V1" tcFormat="NDF" offset="${offset}" duration="${duration}" start="${start}" format="${videoFormat}" ref="${this.escapeXml(assetRef)}" enabled="1" name="${this.escapeXml(videoClip.fileName)}">\n`;

        // Find overlapping audio clips
        const videoStart = videoClip.startTimeInSeconds;
        const videoEnd = videoClip.startTimeInSeconds + videoClip.durationInSeconds;
        const overlappingAudio = audioClips.filter(audioClip => {
            const audioStart = audioClip.startTimeInSeconds;
            const audioEnd = audioClip.startTimeInSeconds + audioClip.durationInSeconds;
            // Check for any overlap
            return audioStart < videoEnd && audioEnd > videoStart;
        });

        console.log(`🎬 FCPXML HIERARCHICAL: Video clip ${videoClip.fileName} has ${overlappingAudio.length} overlapping audio clips`);

        // Add nested audio clips with negative lane numbers
        let audioLayerCounter = -1;
        overlappingAudio.forEach(audioClip => {
            const audioAssetInfo = uniqueFiles.get(audioClip.fileName);
            if (!audioAssetInfo) {
                return;
            }

            // Use absolute timeline offsets for FCP compatibility
            const audioDuration = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.durationInSeconds, options.frameRate);
            const audioOffset = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.startTimeInSeconds, options.frameRate);
            const audioStart = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.sourceStartInSeconds, options.frameRate);
            const audioRole = this._determineAudioRole(audioClip);

            xml += `              <asset-clip videoRole="" tcFormat="NDF" offset="${audioOffset}" duration="${audioDuration}" start="${audioStart}" format="${videoFormat}" ref="${this.escapeXml(audioAssetInfo.assetId)}" enabled="1" name="${this.escapeXml(audioClip.fileName)}" lane="${audioLayerCounter}">\n`;
            xml += `                <adjust-volume amount="0dB"/>\n`;
            xml += `              </asset-clip>\n`;

            console.log(`🎬 FCPXML HIERARCHICAL: Added nested audio ${audioClip.fileName} at lane ${audioLayerCounter}`);
            audioLayerCounter--;
        });

        xml += `            </asset-clip>\n`;

        return { xml, audioLayerCount: overlappingAudio.length };
    },

    /**
     * Generate a gap element with nested audio clips as children
     * @private
     * @param {Number} gapStart - Gap start time in seconds
     * @param {Number} gapEnd - Gap end time in seconds
     * @param {Array} audioClips - All audio-only clips
     * @param {Map} uniqueFiles - Asset information map
     * @param {Object} options - Export options
     * @returns {String} Gap XML content with nested audio
     */
    _generateGapWithNestedAudio: function(gapStart, gapEnd, audioClips, uniqueFiles, options) {
        const gapDuration = gapEnd - gapStart;
        if (gapDuration < 0.01) {
            return ''; // Skip negligible gaps
        }

        const gapDurationFCPXML = TimecodeConverter.convertSecondsToFCPXMLDuration(gapDuration, options.frameRate);
        const gapOffset = TimecodeConverter.convertSecondsToFCPXMLDuration(gapStart, options.frameRate);
        const gapStartFCPXML = TimecodeConverter.convertSecondsToFCPXMLDuration(gapStart, options.frameRate);

        // Find audio clips that overlap with this gap
        const overlappingAudio = audioClips.filter(audioClip => {
            const audioStart = audioClip.startTimeInSeconds;
            const audioEnd = audioClip.startTimeInSeconds + audioClip.durationInSeconds;
            return audioStart < gapEnd && audioEnd > gapStart;
        });

        console.log(`🎬 FCPXML HIERARCHICAL: Gap from ${gapStart}s to ${gapEnd}s has ${overlappingAudio.length} audio clips`);

        if (overlappingAudio.length === 0) {
            // Empty gap with no audio
            return `            <gap offset="${gapOffset}" duration="${gapDurationFCPXML}" start="${gapStartFCPXML}" name="Gap"/>\n`;
        }

        const videoFormat = this._getVideoFormatString(options.frameRate);

        let xml = `            <gap offset="${gapOffset}" duration="${gapDurationFCPXML}" start="${gapStartFCPXML}" name="Gap">\n`;

        // Add nested audio clips with negative lane numbers
        let audioLayerCounter = -1;
        overlappingAudio.forEach(audioClip => {
            const audioAssetInfo = uniqueFiles.get(audioClip.fileName);
            if (!audioAssetInfo) {
                return;
            }

            // Use absolute timeline offsets for FCP compatibility
            const audioDuration = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.durationInSeconds, options.frameRate);
            const audioOffset = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.startTimeInSeconds, options.frameRate);
            const audioStart = TimecodeConverter.convertSecondsToFCPXMLDuration(audioClip.sourceStartInSeconds, options.frameRate);
            const audioRole = this._determineAudioRole(audioClip);

            xml += `              <asset-clip videoRole="" tcFormat="NDF" offset="${audioOffset}" duration="${audioDuration}" start="${audioStart}" format="${videoFormat}" ref="${this.escapeXml(audioAssetInfo.assetId)}" enabled="1" name="${this.escapeXml(audioClip.fileName)}" lane="${audioLayerCounter}">\n`;
            xml += `                <adjust-volume amount="0dB"/>\n`;
            xml += `              </asset-clip>\n`;

            console.log(`🎬 FCPXML HIERARCHICAL: Added gap audio ${audioClip.fileName} at lane ${audioLayerCounter}`);
            audioLayerCounter--;
        });

        xml += `            </gap>\n`;

        return xml;
    },

    /**
     * Generate XML for a single clip (deprecated - use nested structure)
     * @private
     * @param {Object} clip - Timeline clip object
     * @param {Object} assetInfo - Asset information
     * @param {Object} options - Export options
     * @param {Number} indent - Number of spaces for indentation
     * @returns {String} Clip XML content
     */
    _generateClipXML: function(clip, assetInfo, options, indent) {
        const indentStr = ' '.repeat(indent);
        const duration = TimecodeConverter.convertSecondsToFCPXMLDuration(clip.durationInSeconds, options.frameRate);
        const offset = TimecodeConverter.convertSecondsToFCPXMLDuration(clip.startTimeInSeconds, options.frameRate);
        const start = TimecodeConverter.convertSecondsToFCPXMLDuration(clip.sourceStartInSeconds, options.frameRate);
        
        // Use asset-clip format that references the asset directly
        const clipXML = `${indentStr}<asset-clip ref="${this.escapeXml(assetInfo.assetId)}" offset="${offset}" name="${this.escapeXml(clip.fileName || 'clip')}" duration="${duration}" start="${start}"/>\n`;
        
        return clipXML;
    },

    /**
     * Get appropriate video format string for FCPXML based on frame rate
     * @private
     * @param {Number} frameRate - Target frame rate
     * @returns {String} FCPXML video format identifier
     */
    _getVideoFormatString: function(frameRate) {
        // Map frame rates to FCPXML format strings
        const formatMap = {
            23.976: 'FFVideoFormat1080p2398',
            24: 'FFVideoFormat1080p24',
            25: 'FFVideoFormat1080p25',
            29.97: 'FFVideoFormat1080p2997',
            30: 'FFVideoFormat1080p30',
            50: 'FFVideoFormat1080p50',
            59.94: 'FFVideoFormat1080p5994',
            60: 'FFVideoFormat1080p60'
        };
        
        return formatMap[frameRate] || 'FFVideoFormat1080p25'; // Default to 25fps
    },

    /**
     * Get human-readable video format name for FCPXML based on frame rate
     * @private
     * @param {Number} frameRate - Target frame rate
     * @returns {String} Human-readable format name
     */
    _getVideoFormatName: function(frameRate) {
        // Map frame rates to readable format names
        const formatNameMap = {
            23.976: 'FFVideoFormat1080p23.98',
            24: 'FFVideoFormat1080p24',
            25: 'FFVideoFormat1080p25',
            29.97: 'FFVideoFormat1080p29.97',
            30: 'FFVideoFormat1080p30',
            50: 'FFVideoFormat1080p50',
            59.94: 'FFVideoFormat1080p59.94',
            60: 'FFVideoFormat1080p60'
        };
        
        return formatNameMap[frameRate] || 'FFVideoFormat1080p25';
    },

    /**
     * Get frame duration for FCPXML format based on frame rate
     * @private
     * @param {Number} frameRate - Target frame rate
     * @returns {String} Frame duration in rational time format
     */
    _getFrameDuration: function(frameRate) {
        // Map frame rates to frame duration (1/frameRate)
        const frameDurationMap = {
            23.976: '1001/24000s',
            24: '1/24s',
            25: '1/25s',
            29.97: '1001/30000s',
            30: '1/30s',
            50: '1/50s',
            59.94: '1001/60000s',
            60: '1/60s'
        };
        
        return frameDurationMap[frameRate] || '1/25s'; // Default to 25fps
    },

    /**
     * Get enhanced audio file path for FCPXML export
     * @private
     * @param {String} originalFileName - Original source file name
     * @param {String} originalPath - Original source file path
     * @returns {String} Path to enhanced audio file
     */
    _getEnhancedAudioPath: function(originalFileName, originalPath) {
        // For now, generate enhanced audio filename by replacing extension
        const baseName = originalFileName.replace(/\.[^/.]+$/, "");
        const enhancedAudioFileName = `${baseName}_enhanced.wav`;
        
        // If original is in media/ folder, put enhanced audio there too
        if (originalPath.includes('media/')) {
            return `media/${enhancedAudioFileName}`;
        }
        
        // Otherwise use same directory structure as original
        return enhancedAudioFileName;
    },

    /**
     * Determine the appropriate audio role for a clip based on its content
     * @private
     * @param {Object} clip - Timeline clip object
     * @returns {String} Audio role ('dialogue', 'music', 'effects')
     */
    _determineAudioRole: function(clip) {
        // Default roles based on content type
        if (clip.hasVideo) {
            return 'dialogue'; // Video clips typically contain speech
        }
        
        // Audio-only content - determine based on filename and node type
        const fileName = (clip.fileName || '').toLowerCase();
        
        // Check for sound effects indicators
        if (fileName.includes('effect') || 
            fileName.includes('sfx') || 
            fileName.includes('sound') ||
            fileName.includes('0003') || // Legacy convention
            clip.nodeType === 'audio' && fileName.includes('insert')) {
            return 'effects';
        }
        
        // Check for music indicators
        if (fileName.includes('music') || 
            fileName.includes('song') || 
            fileName.includes('background') ||
            fileName.includes('bg') ||
            clip.nodeType === 'wrap' || // Wrap nodes are typically background music
            clip.nodeType === 'music') {
            return 'music';
        }
        
        // Default to music for unidentified audio-only content
        return 'music';
    },

    /**
     * Get the appropriate asset reference for a clip
     * @private
     * @param {Object} clip - Timeline clip object
     * @param {Object} assetInfo - Asset information
     * @param {Object} options - Export options
     * @returns {String} Asset reference ID
     */
    _getAssetReference: function(clip, assetInfo, options) {
        // For video clips with enhanced audio, use video-only asset
        if (clip.hasVideo && options.useEnhancedAudio !== false) {
            return `${assetInfo.assetId}_video`;
        }
        
        // For all other cases, use the main asset
        return assetInfo.assetId;
    },

    /**
     * Format asset source path for FCPXML based on export context
     * @private
     * @param {String} sourceFilePath - Original source file path or URL
     * @returns {String} Properly formatted path for FCPXML
     */
    _formatAssetSourcePath: function(sourceFilePath) {
        // Check if this is a URL (http/https/blob)
        if (sourceFilePath.startsWith('http') || sourceFilePath.startsWith('blob:')) {
            // For URLs, use as-is (no file:// prefix needed for URLs)
            return sourceFilePath;
        }
        
        // For absolute file paths, add file:// prefix
        if (sourceFilePath.startsWith('/')) {
            // Already an absolute path
            return `file://${sourceFilePath}`;
        }
        
        // For relative paths (like "media/filename.mp4"), use as-is without file:// prefix
        // FCPXML handles relative paths within the same directory structure
        return sourceFilePath;
    },

    /**
     * Get supported FCPXML features
     * @returns {Object} Supported features and capabilities
     */
    getSupportedFeatures: function() {
        return {
            version: '1.11',
            supports: {
                video: true,
                audio: true,
                multiLane: true,
                timecode: true,
                assets: true,
                transitions: false, // Basic clips only for now
                effects: false,     // Basic clips only for now
                titles: false       // Basic clips only for now
            },
            frameRates: [23.976, 24, 25, 29.97, 30, 50, 59.94, 60],
            videoFormats: [
                'FFVideoFormat1080p2398',
                'FFVideoFormat1080p24',
                'FFVideoFormat1080p25',
                'FFVideoFormat1080p2997',
                'FFVideoFormat1080p30',
                'FFVideoFormat1080p50',
                'FFVideoFormat1080p5994',
                'FFVideoFormat1080p60'
            ]
        };
    },

    /**
     * Validate FCPXML-specific options
     * @param {Object} options - Export options
     * @returns {Object} Validated options
     */
    validateAndExtendOptions: function(options) {
        const validatedOptions = BaseExporter.prototype.validateAndExtendOptions.call(this, options);
        
        // Ensure frame rate is supported
        const supportedFrameRates = this.getSupportedFeatures().frameRates;
        if (!supportedFrameRates.includes(validatedOptions.frameRate)) {
            // Find closest supported frame rate
            validatedOptions.frameRate = TimecodeConverter.getClosestStandardFrameRate(validatedOptions.frameRate);
        }
        
        return validatedOptions;
    },

    /**
     * Override getMediaFilePath to handle both loaded and non-loaded project contexts
     * FCPXML should reference accessible media paths for Final Cut Pro relinking
     * @param {Object} clip - Timeline clip object
     * @param {Object} options - Export options
     * @returns {String} Media file path
     */
    getMediaFilePath: function(clip, options) {
        // Check if we should use relative paths (for zip packages with media files)
        if (options.mediaPathType === 'relative' && clip.fileName) {
            return `media/${clip.fileName}`;
        }
        
        // For FCPXML, we need a path that Final Cut Pro can potentially relink to
        // Priority order: original URL > fallback filename > generated name
        const sourceUrl = clip.sourceMediaUrl;
        
        // If we have a valid HTTP URL (not a blob), use it
        if (sourceUrl && sourceUrl.startsWith('http')) {
            return sourceUrl;
        }
        
        // If we have a valid blob URL (editor context), use it
        if (sourceUrl && sourceUrl.startsWith('blob:')) {
            return sourceUrl;
        }
        
        // If we have a valid file path, use it
        if (sourceUrl && !sourceUrl.includes('blob:') && sourceUrl.length > 0) {
            return sourceUrl;
        }
        
        // Fallback: Use filename if available
        if (clip.fileName && clip.fileName !== 'unknown_file') {
            // For non-loaded projects, generate a reasonable path
            // Final Cut Pro can ask user to relink these
            return `media/${clip.fileName}`;
        }
        
        // Last resort: Generate a descriptive filename based on timeline position
        const timeBasedName = `trebble_clip_${Math.round(clip.startTimeInSeconds)}_${Math.round(clip.durationInSeconds)}s.${clip.hasVideo ? 'mp4' : 'mp3'}`;
        return `media/${timeBasedName}`;
    },

    /**
     * Get FCPXML-specific default options
     * @returns {Object} FCPXML format defaults
     */
    getDefaultOptions: function() {
        return _.extend(BaseExporter.prototype.getDefaultOptions.call(this), {
            frameRate: 25,
            projectTitle: 'Trebble FCPXML Project',
            mediaPathType: 'url', // FCPXML works well with full URLs
            useEnhancedAudio: false // Disable enhanced audio until files are generated
        });
    },

    /**
     * Generate FCPXML project info
     * @param {Object} options - Export options (optional)
     * @returns {Object} Project information for FCPXML
     */
    getProjectInfo: function(options) {
        options = options || {};

        return {
            format: this._getVideoFormatString(options.frameRate),
            frameRate: options.frameRate,
            version: '1.8',
            compatible: ['Final Cut Pro X', 'DaVinci Resolve (via FCPXML)', 'Motion'],
            features: [
                'Multi-lane timeline',
                'Frame-accurate timing',
                'Asset references',
                'Audio/video sync'
            ]
        };
    }

});

export default FcpxmlExporter;